import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import { useEffect, useState, useContext } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  NavDropdown
} from "react-bootstrap";
import {
  encryptStringWithRsaPublicKey,
  MAXIMUM_CLEAR_PASSWORD,
  getQualityCheckStatusKeyFromValue
} from "../../helpers/stringHelper";
import {
  QualityCheckStatusUI,
  QualityCheckStatusType,
  ExtendedRepositoryObject
} from "../../backend/types";
import Error from "../abstractComponents/error";
import Success from "../abstractComponents/success";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import { useGetKarapitStudyList } from "../../backend/hooks/importExportMenu/karapit/queryGetKarapitStudyList";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import { useImportKarapit } from "../../backend/hooks/importExportMenu/karapit/mutationImportKarapit";

const FILE_TYPES = [
  ".dm",
  "randomization list 1",
  "randomization list 2",
  ".ba",
  ".ba original",
  "shipment list CRO",
  "shipment list PRA",
  "Primary Reconciliation Report"
];

// Component that renders everything related to Karapit Import
export default function ImportFromKarapit(props: {
  currentFolder: string;
  disabled: boolean;
  currentSubArray: ExtendedRepositoryObject[];
}) {
  const { activityId, activityData } = useContext(PmxActivityDetailsContext);

  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [qualityCheckStatus, setQualityCheckStatus] =
    useState<QualityCheckStatusUI>(QualityCheckStatusUI.NOT_STARTED);

  const [description, setDescription] = useState("");
  const [karapitUsername, setKarapitUsername] = useState(
    localStorage.getItem("karapitUsername") || ""
  );
  const [karapitPassword, setKarapitPassword] = useState(
    localStorage.getItem("karapitPassword") || ""
  );
  const [study, setStudy] = useState("");
  const [files, setFiles] = useState<string[]>([]);

  const handleChangeStudy = (event: SelectChangeEvent) => {
    setStudy(event.target.value);
  };
  const handleChangeFile = (event: SelectChangeEvent<typeof files>) => {
    const {
      target: { value }
    } = event;
    setFiles(typeof value === "string" ? value.split(",") : value);
  };

  const { getKarapitStudyList, studyList, isLoadingStudyList, errorStudyList } =
    useGetKarapitStudyList();

  const {
    importContentMutation,
    karapitImportMutationData,
    isLoadingKarapitMutation,
    errorKarapitMutation
  } = useImportKarapit();

  const closeModal = () => {
    setIsImportModalVisible(false);
    setQualityCheckStatus(QualityCheckStatusUI.NOT_STARTED);
    setDescription("");
    setFiles([]);
    setStudy("");
  };

  function changeQualityCheckedStatus(
    event: React.ChangeEvent<HTMLSelectElement>
  ) {
    event.preventDefault();
    setQualityCheckStatus(
      QualityCheckStatusUI[event.target.value as QualityCheckStatusType]
    );
  }

  const login = () => {
    let newPassword = karapitPassword;

    if (
      karapitPassword.length < MAXIMUM_CLEAR_PASSWORD &&
      !karapitPassword.endsWith("=")
    ) {
      //Password is not encrypted, so we encrypt it
      newPassword = encryptStringWithRsaPublicKey(karapitPassword);
      setKarapitPassword(newPassword);
      localStorage.setItem("karapitUsername", karapitUsername);
      localStorage.setItem("karapitPassword", karapitPassword);
    }

    getKarapitStudyList({
      variables: {
        username: karapitUsername,
        encryptedPassword: newPassword
      }
    });
  };

  const importAll = () => {
    importContentMutation({
      variables: {
        username: karapitUsername,
        encryptedPassword: karapitPassword,
        targetFolder: props.currentFolder,
        activityId: activityId,
        qualityCheckStatus:
          getQualityCheckStatusKeyFromValue(qualityCheckStatus),
        description: description,
        files: files,
        study: study
      }
    }).then(() => {
      closeModal();
    });
  };

  return (
    <>
      <NavDropdown.Item
        size="small"
        variant="outlined"
        onClick={() => {
          setIsImportModalVisible(true);
        }}
        disabled={props.disabled}
        id="importFromKarapitItem"
      >
        <AutoGraphIcon /> Karapit Import
      </NavDropdown.Item>

      {karapitImportMutationData?.importKarapitContent === true && (
        <Success message="Karapit import started. You will receive a notification in up to 15 Minutes" />
      )}
      {(errorStudyList || errorKarapitMutation) && (
        <Error error={errorStudyList || errorKarapitMutation} />
      )}

      <Modal
        show={isImportModalVisible}
        onHide={closeModal}
        enforceFocus={false}
        dialogClassName="modal-80w"
        aria-labelledby="importKarapitTitle"
      >
        <Modal.Header closeButton>
          <Modal.Title id="importFromKarapitTitle">
            Import from Karapit into &quot;{activityData.trialNumber}
            {props.currentFolder}&quot;
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container fluid>
            <Row
              className={
                karapitPassword.length > MAXIMUM_CLEAR_PASSWORD
                  ? "login_grey_color_row"
                  : ""
              }
            >
              <Col xs={2}>
                <Form.Label>Karapit Username:</Form.Label>
              </Col>
              <Col xs={3}>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter username"
                  id="importFromKarapitUserFormControl"
                  value={karapitUsername}
                  onChange={(e) => setKarapitUsername(e.target.value)}
                />
              </Col>
            </Row>
            <Row
              className={
                karapitPassword.length > MAXIMUM_CLEAR_PASSWORD
                  ? "login_grey_color_row"
                  : ""
              }
            >
              <Col xs={2}>
                <Form.Label>Karapit Password:</Form.Label>
              </Col>
              <Col xs={3}>
                <Form.Control
                  type="password"
                  required
                  placeholder="Enter password"
                  id="importFromKarapitPasswordFormControl"
                  value={karapitPassword}
                  onChange={(e) => setKarapitPassword(e.target.value)}
                />
              </Col>
              <Col xs={1}>
                <Button
                  variant="primary"
                  onClick={login}
                  id="loginKarapitButton"
                  disabled={
                    karapitUsername === "" ||
                    karapitPassword === "" ||
                    karapitPassword.length > MAXIMUM_CLEAR_PASSWORD
                  }
                >
                  Login
                </Button>
              </Col>
            </Row>

            {(isLoadingStudyList || isLoadingKarapitMutation) && (
              <div className="center_div">
                <Spinner animation="border" className="spinner_color" />
                <p>Loading...</p>
              </div>
            )}
            {studyList && (
              <>
                <Row>
                  <Col xs={2}>
                    <Form.Label>Study:</Form.Label>
                  </Col>
                  <Col xs={3}>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                      <InputLabel id="study-select-small-label">
                        Study
                      </InputLabel>
                      <Select
                        labelId="study-select-small-label"
                        id="study_select_small"
                        value={study}
                        label="Age"
                        onChange={handleChangeStudy}
                      >
                        {studyList?.getKarapitStudyList.map((study: string) => (
                          <MenuItem
                            value={study}
                            key={study}
                            selected={study === activityData.trialNumber}
                          >
                            {study}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Col>
                </Row>
                <Row>
                  <Col xs={2}>
                    <Form.Label>File Imports:</Form.Label>
                  </Col>
                  <Col xs={3}>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                      <InputLabel id="file-select-small-label">
                        Files
                      </InputLabel>
                      <Select
                        labelId="file-select-small-label"
                        id="file_select_small"
                        multiple
                        value={files}
                        label="Age"
                        onChange={handleChangeFile}
                        input={<OutlinedInput label="Tag" />}
                        renderValue={(selected) => selected.join(", ")}
                      >
                        {FILE_TYPES.map((name) => (
                          <MenuItem key={name} value={name}>
                            <Checkbox checked={files.includes(name)} />
                            <ListItemText primary={name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Col>
                </Row>
                <Row>
                  <Col xs={2}>
                    <Form.Label>Import Description:</Form.Label>
                  </Col>
                  <Col xs={3}>
                    <Form.Control
                      type="text"
                      required
                      placeholder="Enter description"
                      id="importFromKarapitDescriptionFormControl"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={2}>
                    <Form.Label>Quality Controlled Status</Form.Label>
                  </Col>
                  <Col xs={3}>
                    <Form.Select
                      value={String(
                        getQualityCheckStatusKeyFromValue(qualityCheckStatus)
                      )}
                      onChange={changeQualityCheckedStatus}
                      key="karapit_quality_controlled"
                      id="importFromKarapitQualityControlledFormSelect"
                    >
                      {Object.entries(QualityCheckStatusUI).map((element) => (
                        <option key={element[0]} value={element[0]}>
                          {element[1]}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
                <br />
                <br />
                <Row>
                  <Col xs={3}>
                    <Button
                      variant="primary"
                      onClick={importAll}
                      id="finishImportFromKarapitButton"
                      disabled={study === "" || files.length === 0}
                    >
                      Import Selected Files
                    </Button>
                  </Col>
                </Row>
                <br />
              </>
            )}
          </Container>
        </Modal.Body>
      </Modal>
    </>
  );
}
